import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const PermissionsContext = createContext(null);

// Sidebar "Management" group — write actions are super-admin-only (see can() below).
export const SUPER_ADMIN_WRITE_MODULES = new Set([
  "panels_management", "device_management", "site_management", "floor_plans", "hardware_gateways",
]);

// Returns { perms, can, loading }
// can(moduleKey, action) → bool   (action = 'view'|'create'|'update'|'delete')
// Platform roles (super_admin, platform_support) always return true.
export function usePermissions() {
  return useContext(PermissionsContext) || { perms: {}, can: () => true, loading: false };
}

export function PermissionsProvider({ user, children }) {
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);

  const isPlatform = user?.role === "super_admin" || user?.role === "platform_support";

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    if (isPlatform) { setLoading(false); return; } // platform = all access, skip fetch
    api.get("/me/permissions")
      .then((r) => setPerms(r.data || {}))
      .catch(() => setPerms({}))
      .finally(() => setLoading(false));
  }, [user?.role]);

  function can(moduleKey, action) {
    // Management records are maintained by the platform: only a super admin may add, edit or
    // delete them, whatever the per-org permission matrix says (the backend enforces the same —
    // these endpoints are require_role("super_admin")). Viewing is unaffected.
    if (SUPER_ADMIN_WRITE_MODULES.has(moduleKey) && action !== "view") return user?.role === "super_admin";
    if (isPlatform) return true;
    const actionKey = `can_${action}`;
    return perms[moduleKey]?.[actionKey] ?? false;
  }

  return (
    <PermissionsContext.Provider value={{ perms, can, loading }}>
      {children}
    </PermissionsContext.Provider>
  );
}
